import {
  HarmReport,
  InterventionStatus,
  ReviewStatus
} from '@inplay/models';
import { ReportDetailsConfig } from './reportDetails.types';
import {
  formatMinutesToHoursAndMinutes,
  formatDateWithTime,
  getDisplayName,
} from '../../utils/helper';
import {
  getHarmReportsInterventionDecisionDotColor,
  getHarmReportsInterventionStatusDotColor,
} from '../../pages/harmReports/harmReportesHelper';


export const createHarmReportConfig = (
  harmReport: HarmReport,
): ReportDetailsConfig => {
  const is1stInstance = harmReport.harmInstanceCountForPlayer === '1st';
  const isReviewed = Boolean(harmReport.reviewedBy);
  const isAutoGenerated = !harmReport.createdBy;

  const isExpired = harmReport.expiresAt && new Date(harmReport.expiresAt).getTime() <= Date.now();
  const showDeadline = !isReviewed && Boolean(harmReport.expiresAt) && !isExpired;

  const rightColumnFields = [
    {
      labelKey: 'harmReports.details.fields.reviewStatus',
      value: harmReport.reviewStatus,
      column: 2 as const,
      isStatus: true,
      dotColor: getHarmReportsInterventionStatusDotColor(
        harmReport.reviewStatus === ReviewStatus.COMPLETE
          ? InterventionStatus.COMPLETE
          : harmReport.reviewStatus === ReviewStatus.PENDING
            ? InterventionStatus.PENDING
            : InterventionStatus.NOT_APPLICABLE,
      ),
    },
    {
      labelKey: 'reportDetails.interventionDecision',
      value: harmReport.intervention.decision,
      column: 2 as const,
      isStatus: true,
      dotColor: getHarmReportsInterventionDecisionDotColor(
        harmReport.intervention.decision,
      ),
    },
    {
      labelKey: 'reportDetails.interventionStatus',
      value: harmReport.intervention.status,
      column: 2 as const,
      isStatus: true,
      dotColor: getHarmReportsInterventionStatusDotColor(
        harmReport.intervention.status,
      ),
    },
    {
      labelKey: 'harmReports.details.fields.interventionReportRefNo',
      value: harmReport.intervention.refNo || '-',
      column: 2 as const,
      isLinkedReports: Boolean(harmReport.intervention.id),
      linkedReports: harmReport.intervention.id
        ? [{ id: harmReport.intervention.id, refNo: harmReport.intervention.refNo || "View Report" }]
        : undefined,
    },
    ...(showDeadline
      ? [
        {
          labelKey: is1stInstance
            ? 'harmReports.details.fields.interventionDeadline'
            : 'harmReports.details.fields.reviewDeadline',
          value: '',
          column: 2 as const,
          className: 'text-red-500',
          isCountdown: true,
          expiresAt: harmReport.expiresAt,
        },
      ]
      : []),
  ];

  return {
    titleKey: 'harmReports.details.title',
    reportRefNo: harmReport.reportRefNo,
    sections: [
      {
        id: 'generalInfo',
        titleKey: 'reportDetails.generalInfo',
        type: 'general-info',
        data: {
          fields: [
            {
              labelKey: 'reportDetails.reportNo',
              value: harmReport.reportRefNo,
              column: 1,
            },
            {
              labelKey: 'reportDetails.venue',
              value: harmReport.venueBranch.name,
              column: 1,
            },
            {
              labelKey: 'harmReports.details.fields.harmIdentifiedBy',
              value: getDisplayName(harmReport.identifiedBy),
              column: 1,
            },
            {
              labelKey: 'harmReports.details.fields.harmIdentifiedOn',
              value: formatDateWithTime(harmReport.identifiedAt) || '-',
              column: 1,
            },
            {
              labelKey: 'harmReports.details.fields.harmInstance',
              value: harmReport.harmInstanceCountForPlayer,
              column: 1,
            },
            ...rightColumnFields,
          ],
        },
      },
      {
        id: 'playerPhotos',
        titleKey: 'reportDetails.playerPhotos',
        type: 'player-photos',
        data: {
          faceImage: harmReport.playerProfile.faceImage,
          bodyImage: harmReport.playerProfile.bodyImage,
        },
      },
      {
        id: 'playerDetails',
        titleKey: 'reportDetails.playerDetails',
        type: 'player-details',
        data: {
          leftColumn: [
            {
              labelKey: 'reportDetails.playerRefNo',
              value: harmReport.playerProfile.id.slice(0, 5),
            },
            {
              labelKey: 'reportDetails.nameNickname',
              value: harmReport.playerDetails.name || '-',
            },
            {
              labelKey: 'reportDetails.description',
              value: harmReport.playerDetails.description || '-',
            },
            {
              labelKey: 'reportDetails.currentUninterruptedTime',
              value: formatMinutesToHoursAndMinutes(
                harmReport.playerDetails.gambling?.uninterruptedTime,
              ),
            },
            {
              labelKey: 'reportDetails.gamblingAtmUse',
              value: harmReport.playerDetails.gambling?.gamblingATMUse,
            },
          ],
          rightColumn: [
            {
              labelKey: 'reportDetails.consecutiveSweepPresence',
              value: harmReport.playerDetails.gambling?.consecutiveSweepsCount,
            },
            {
              labelKey: 'reportDetails.totalInterventions',
              value: `${harmReport.playerDetails.gambling?.interventionsOffered} / ${harmReport.playerDetails.gambling?.interventionsRequested}`,
            },
            {
              labelKey: 'reportDetails.totalTimeInGambling',
              value: formatMinutesToHoursAndMinutes(
                harmReport.playerDetails.gambling?.totalTimeInGameArea,
              ),
            },
            {
              labelKey: 'reportDetails.totalTimeOutside',
              value: formatMinutesToHoursAndMinutes(
                harmReport.playerDetails.gambling?.totalTimeOutsideGameArea,
              ),
            },
          ],
        },
      },
      {
        id: 'signsOfGamblingHarm',
        titleKey: 'reportDetails.signsOfGamblingHarm',
        type: 'harm-signs',
        data: {
          lengthOfPlay: harmReport.signsOfGamblingHarm.lengthOfPlay,
          money: harmReport.signsOfGamblingHarm.money,
          behaviour: harmReport.signsOfGamblingHarm.behaviour,
          other: harmReport.signsOfGamblingHarm.other,
        },
      },
      {
        id: 'notesObservations',
        titleKey: 'reportDetails.notesObservations',
        type: 'notes',
        data: {
          content: harmReport.createdBy?.notes || '-',
        },
      },
      {
        id: 'createdBy',
        titleKey: 'reportDetails.createdBy',
        type: 'signature',
        data: {
          signature: harmReport.createdBy,
          isAutoGenerated,
          autoGeneratedDate: harmReport.createdAt,
        },
      },
    ],
    hasReviewSection: Boolean(harmReport.reviewedBy),
    hideReviewAndSign: harmReport.reviewStatus === 'N/A' || Boolean(harmReport.reviewedBy),
    reviewData: harmReport.reviewedBy
      ? {
        interventionRequired: harmReport.intervention.required,
        comments: harmReport.reviewedBy.notes,
        reviewedBy: harmReport.reviewedBy,
      }
      : undefined,
  };
};
